import { useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createPurchaseOrderAmendment,
  deletePurchaseOrderAmendment,
  fetchPurchaseOrderAmendmentDetail,
  submitPurchaseOrderAmendment,
  updatePurchaseOrderAmendment,
} from '#/api/srm/purchaseOrder/amendment';
import { fetchPurchaseOrderDetail } from '#/api/srm/purchaseOrder/fetchDetail';
import { fetchPurchaseOrderPaymentPlan } from '#/api/srm/purchaseOrder/fetchPaymentPlan';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import PurchaseAmendmentPaymentPlanEditor from '#/modules/srmMgmt/purchaseOrder/components/PurchaseAmendmentPaymentPlanEditor';
import PurchaseAmendmentStatusBadge from '#/modules/srmMgmt/purchaseOrder/components/PurchaseAmendmentStatusBadge';
import PurchaseOrderAmendmentLinesEditor from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderAmendmentLinesEditor';
import {
  buildPurchaseOrderAmendmentPayload,
  mapPurchaseOrderAmendmentToFormValues,
  mapPurchaseOrderDetailToAmendmentFormValues,
} from '#/modules/srmMgmt/purchaseOrder/helper/amendmentFormSerialize';
import {
  getPurchaseOrderApiErrorMessage,
  isPurchaseOrderVersionConflict,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import { computePurchaseOrderAmount } from '#/modules/srmMgmt/purchaseOrder/helper/formSerialize';
import {
  emptyPaymentPlanFormValues,
  mapPaymentPlanToFormValues,
  pickPaymentPlanForWrite,
  validatePaymentPlanBeforeSubmit,
} from '#/modules/srmMgmt/purchaseOrder/helper/paymentPlanForm';
import { PURCHASE_AMENDMENT_KIND } from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentKind';
import {
  PURCHASE_AMENDMENT_STATUS,
  findOpenPurchaseAmendment,
  isPurchaseAmendmentDraft,
  pickPurchaseAmendmentUpdatedAt,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentStatus';
import { canAmendPurchaseOrder } from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';
import {
  PURCHASE_ORDER_AMENDMENT_FIELD_LABELS,
  PURCHASE_ORDER_TAX_TYPE_OPTIONS,
  purchaseOrderAmendmentFormSchema,
} from '#/modules/srmMgmt/purchaseOrder/schemas/purchaseOrderAmendmentSchema';
import { formatDisplayAmount } from '#/utils/formatNumber';
import {
  purchaseAmendmentDetailQueryKey,
  purchaseOrderDetailQueryKey,
  purchaseOrderListQueryKeyPrefix,
  purchasePaymentPlanQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const KIND = PURCHASE_AMENDMENT_KIND.PURCHASE_ORDER;

const DEFAULT_VALUES = {
  taxType: 'TAX_EXCLUDED',
  taxRate: 5,
  roundingMode: 'HALF_UP',
  issueReason: '',
  remark: '',
  lines: [],
  paymentPlan: emptyPaymentPlanFormValues(),
};

function pickUpdatedAt(detail) {
  return detail?.record?.updatedAt ?? detail?.updatedAt ?? null;
}

/** 行金額＝(訂購量－取消量)×單價；表單預覽用（後端仍以保存結果為準）。 */
function computeAmendmentAmount(lines, taxType, taxRate) {
  const effectiveLines = (lines ?? []).map((line) => ({
    ...line,
    orderQty: (Number(line?.orderQty) || 0) - (Number(line?.cancelQty) || 0),
  }));
  return computePurchaseOrderAmount(effectiveLines, taxType, taxRate);
}

/**
 * 物料採購異動單：建立／編輯草稿，提交後經核准才套用。
 * 改變應付總額時須一併附完整付款計畫；不改供應商／幣別／匯率／到貨日。
 */
const CreateEditPurchaseOrderAmendmentPage = () => {
  const { id: purchaseOrderId, amendmentId } = useParams();
  const isEditMode = Boolean(amendmentId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canUpdate = useCan(PERM.PURCHASE_ORDER_UPDATE);
  const orderUpdatedAtRef = useRef(null);
  const amendmentUpdatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
    queryFn: () => fetchPurchaseOrderDetail(purchaseOrderId),
    enabled: Boolean(purchaseOrderId),
  });

  const amendmentQuery = useQuery({
    queryKey: purchaseAmendmentDetailQueryKey(KIND, purchaseOrderId, amendmentId),
    queryFn: () => fetchPurchaseOrderAmendmentDetail(purchaseOrderId, amendmentId),
    enabled: Boolean(purchaseOrderId && amendmentId),
  });

  const paymentPlanQuery = useQuery({
    queryKey: purchasePaymentPlanQueryKey(KIND, purchaseOrderId),
    queryFn: () => fetchPurchaseOrderPaymentPlan(purchaseOrderId),
    enabled: Boolean(purchaseOrderId),
    retry: false,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(purchaseOrderAmendmentFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });
  const watchedTaxType = useWatch({ control, name: 'taxType' });
  const watchedTaxRate = useWatch({ control, name: 'taxRate' });

  const amountPreview = useMemo(
    () => computeAmendmentAmount(watchedLines, watchedTaxType, watchedTaxRate),
    [watchedLines, watchedTaxType, watchedTaxRate],
  );

  const detail = detailQuery.data?.data;
  // 原單金額以同一套預覽公式計算，避免與後端 rounding 差異誤判為「總額已變」
  const originalPreview = useMemo(() => {
    if (!detail) {
      return null;
    }
    const original = mapPurchaseOrderDetailToAmendmentFormValues(detail);
    return computeAmendmentAmount(original.lines, original.taxType, original.taxRate);
  }, [detail]);
  const amendment = amendmentQuery.data?.data;
  const currentPlan = paymentPlanQuery.data?.data ?? null;
  const currentPlanReady = !paymentPlanQuery.isLoading;

  useEffect(() => {
    if (!detail || !currentPlanReady) {
      return;
    }
    if (isEditMode && !amendment) {
      return;
    }
    const originalValues = mapPurchaseOrderDetailToAmendmentFormValues(detail);
    orderUpdatedAtRef.current = pickUpdatedAt(detail);
    amendmentUpdatedAtRef.current = pickPurchaseAmendmentUpdatedAt(amendment);
    resetValidationSummary();
    reset({
      ...(isEditMode
        ? mapPurchaseOrderAmendmentToFormValues(amendment, detail)
        : { ...originalValues, issueReason: '', remark: '' }),
      paymentPlan: mapPaymentPlanToFormValues(
        isEditMode ? amendment?.paymentPlan : null,
        currentPlan,
      ),
    });
  }, [detail, amendment, currentPlan, currentPlanReady, isEditMode, reset, resetValidationSummary]);

  function buildWriteBody(values, updatedAt) {
    return buildPurchaseOrderAmendmentPayload(
      { ...values, paymentPlan: pickPaymentPlanForWrite(values.paymentPlan) },
      { updatedAt },
    );
  }

  async function invalidateOrder() {
    await queryClient.invalidateQueries({ queryKey: purchaseOrderListQueryKeyPrefix() });
    await queryClient.invalidateQueries({ queryKey: purchaseOrderDetailQueryKey(purchaseOrderId) });
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, values }) => {
      if (action === 'delete') {
        return deletePurchaseOrderAmendment(purchaseOrderId, amendmentId, {
          updatedAt: amendmentUpdatedAtRef.current,
        });
      }
      const saved = isEditMode
        ? await updatePurchaseOrderAmendment(
          purchaseOrderId,
          amendmentId,
          buildWriteBody(values, amendmentUpdatedAtRef.current),
        )
        : await createPurchaseOrderAmendment(
          purchaseOrderId,
          buildWriteBody(values, orderUpdatedAtRef.current),
        );
      if (action !== 'submit') {
        return saved;
      }
      // 先保存草稿再提交；提交使用異動單自身最新版本
      amendmentUpdatedAtRef.current = pickPurchaseAmendmentUpdatedAt(saved?.data);
      return submitPurchaseOrderAmendment(purchaseOrderId, saved?.data?.id ?? amendmentId, {
        updatedAt: amendmentUpdatedAtRef.current,
      });
    },
    onSuccess: async (result, { action }) => {
      const fallbackMessage = {
        save: '異動草稿已儲存',
        submit: '異動單已提交，待審核',
        delete: '異動草稿已刪除',
      }[action];
      toast.success(result?.message || fallbackMessage);
      await invalidateOrder();

      if (action === 'save' && !isEditMode) {
        const createdId = result?.data?.id;
        if (createdId) {
          navigate(ROUTES.purchaseOrderAmendmentEdit(purchaseOrderId, createdId), { replace: true });
          return;
        }
      }
      if (action === 'save') {
        amendmentUpdatedAtRef.current = pickPurchaseAmendmentUpdatedAt(result?.data);
        await queryClient.invalidateQueries({
          queryKey: purchaseAmendmentDetailQueryKey(KIND, purchaseOrderId, amendmentId),
        });
        return;
      }
      navigate(ROUTES.purchaseOrderDetail(purchaseOrderId), { replace: true });
    },
    onError: async (error) => {
      toast.error(getPurchaseOrderApiErrorMessage(error, '異動失敗'));
      if (isPurchaseOrderVersionConflict(error)) {
        await detailQuery.refetch();
        if (isEditMode) {
          await amendmentQuery.refetch();
        }
      }
    },
  });

  const isPending = commandMutation.isPending;
  const openAmendment = findOpenPurchaseAmendment(detail?.amendments);
  const isPageLoading = detailQuery.isLoading
    || (isEditMode && amendmentQuery.isLoading);
  const pageError = detailQuery.error || (isEditMode ? amendmentQuery.error : null);
  const currentPlanError = paymentPlanQuery.isError
    ? getPurchaseOrderApiErrorMessage(paymentPlanQuery.error, '讀取付款計畫失敗')
    : null;

  if (!isPageLoading && detail && !canAmendPurchaseOrder(detail.status)) {
    return <Navigate to={ROUTES.purchaseOrderDetail(purchaseOrderId)} replace />;
  }

  // 同單同時最多一張在途異動：新建時已有草稿就轉去編輯，已提交則回詳情
  if (!isPageLoading && detail && !isEditMode && openAmendment) {
    if (openAmendment.status === PURCHASE_AMENDMENT_STATUS.DRAFT && openAmendment.id) {
      return (
        <Navigate
          to={ROUTES.purchaseOrderAmendmentEdit(purchaseOrderId, openAmendment.id)}
          replace
        />
      );
    }
    return <Navigate to={ROUTES.purchaseOrderDetail(purchaseOrderId)} replace />;
  }

  if (!isPageLoading && isEditMode && amendment && !isPurchaseAmendmentDraft(amendment)) {
    return <Navigate to={ROUTES.purchaseOrderDetail(purchaseOrderId)} replace />;
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSave(values) {
    markSubmitAttempted();
    if (!isPending) {
      commandMutation.mutate({ action: 'save', values });
    }
  }

  function onSubmitDraft(values) {
    markSubmitAttempted();
    if (isPending) {
      return;
    }
    const planError = validatePaymentPlanBeforeSubmit(values.paymentPlan, {
      originalTotal: originalPreview?.totalAmount,
      nextTotal: amountPreview.totalAmount,
    });
    if (planError) {
      toast.error(planError);
      return;
    }
    commandMutation.mutate({ action: 'submit', values });
  }

  async function handleDelete() {
    const ok = await confirm({
      title: '刪除異動草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (ok && !isPending) {
      commandMutation.mutate({ action: 'delete' });
    }
  }

  const pageTitle = isEditMode ? '編輯物料採購異動單' : '新建物料採購異動單';

  return (
    <PageShell variant="formTall">
      {ConfirmDialogHost}
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              {isEditMode && amendment?.status ? (
                <PurchaseAmendmentStatusBadge status={amendment.status} size="sm" />
              ) : null}
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>返回詳情</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {pageError ? (
            <div className="space-y-3">
              <p className="text-sm text-destructive">
                {getPurchaseOrderApiErrorMessage(pageError, '找不到物料採購單或異動單')}
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.purchaseOrder}>返回列表</Link>
              </Button>
            </div>
          ) : null}

          {!isPageLoading && !pageError && detail ? (
            <>
              <p className="mb-4 text-sm text-muted-foreground">
                {`單號 ${detail.purchaseOrderNo || '—'}；`}
                {isEditMode && amendment?.amendmentNo ? `異動號 ${amendment.amendmentNo}；` : ''}
                儲存為草稿，提交後經核准才套用。
              </p>

              <div className="space-y-4">
                <FormValidationSummary
                  show={isSubmitAttempted}
                  errors={errors}
                  fieldLabels={PURCHASE_ORDER_AMENDMENT_FIELD_LABELS}
                />

                <form
                  className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                  onSubmit={handleSubmit(onSave, handleInvalidSubmit)}
                  noValidate
                >
                  <div className="create_edit_form__body divide-y divide-gray-500 pb-0!">
                    <section className="pb-6">
                      <div className="form-block">
                        <h2 className="form-block__title">異動內容</h2>
                      </div>
                      <div
                        className={cn(
                          'grid grid-cols-1 gap-3',
                          '@4xl/sheet:grid-cols-2',
                        )}
                      >
                        <FloatingTextarea
                          id="issueReason"
                          label="異動事由"
                          register={register}
                          errors={errors}
                          disabled={isPending}
                        />
                        <FloatingTextarea
                          id="remark"
                          label="備註"
                          register={register}
                          errors={errors}
                          disabled={isPending}
                        />
                      </div>
                    </section>

                    <PurchaseOrderAmendmentLinesEditor
                      control={control}
                      register={register}
                      errors={errors}
                      getValues={getValues}
                    />

                    <section className="pb-6">
                      <div className="form-block">
                        <h2 className="form-block__title">金額與稅別</h2>
                      </div>
                      <div
                        className={cn(
                          'grid grid-cols-1 gap-3',
                          '@4xl/sheet:grid-cols-2',
                        )}
                      >
                        <FloatingSelect
                          id="taxType"
                          label="稅別"
                          options={PURCHASE_ORDER_TAX_TYPE_OPTIONS}
                          control={control}
                          errors={errors}
                          isDisabled={isPending}
                        />
                        <FloatingInput
                          id="taxRate"
                          label="稅率（%）"
                          type="number"
                          min={0}
                          step="any"
                          register={register}
                          errors={errors}
                          disabled={isPending}
                        />
                        <div className="min-w-0 @4xl/sheet:col-span-2">
                          <p className="text-xs text-gray-500">異動後總金額（預覽）</p>
                          <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 underline">
                            {formatDisplayAmount(amountPreview.totalAmount)}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {`未稅 ${formatDisplayAmount(amountPreview.subtotalAmount)}`
                              + ` ／ 稅額 ${formatDisplayAmount(amountPreview.taxAmount)}`
                              + ` ／ 原應付 ${formatDisplayAmount(detail.amountSummary?.totalAmount)}`}
                          </p>
                        </div>
                      </div>
                    </section>

                    <PurchaseAmendmentPaymentPlanEditor
                      control={control}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      currentPlan={currentPlan}
                      currentPlanError={currentPlanError}
                      originalTotal={originalPreview?.totalAmount}
                      nextTotal={amountPreview.totalAmount}
                      disabled={isPending}
                    />
                  </div>

                  <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                    <Button type="button" variant="outline" asChild>
                      <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>取消</Link>
                    </Button>
                    {isEditMode && canUpdate ? (
                      <Button
                        type="button"
                        variant="destructive"
                        className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
                        disabled={isPending}
                        onClick={handleDelete}
                      >
                        刪除草稿
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button type="submit" variant="outline" disabled={isPending}>
                        {isPending ? '處理中…' : '儲存草稿'}
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button
                        type="button"
                        disabled={isPending}
                        onClick={handleSubmit(onSubmitDraft, handleInvalidSubmit)}
                      >
                        提交審核
                      </Button>
                    ) : null}
                  </div>
                </form>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditPurchaseOrderAmendmentPage;
